/**
 * The chip-based predicate builder (ADR-038 Phase F; REQ-UI-114..117).
 *
 * The composed query stays visible above the results after it runs
 * (REQ-UI-116), so the builder is a persistent strip of chips rather than
 * a modal or a collapsing form. Each chip reads
 * ``attribute operator value`` and opens a popover editor on click;
 * committing an edit re-runs the query automatically when the whole query
 * validates (D14) — the debounce and the gate live in the panel, so this
 * component stays a controlled editor with no timing of its own.
 *
 * AND / OR grouping is expressed structurally: a group is a bordered
 * container with its own operator toggle and its own add affordances, so
 * the boolean shape an operator built is the boolean shape they see. The
 * toggle labels the group's operator rather than putting a conjunction
 * between chips, because "A and B or C" reads ambiguously and the tree
 * never is.
 *
 * Errors render **against the offending chip** (REQ-UI-117): a validation
 * failure marks that chip and shows its message, rather than failing the
 * whole query opaquely.
 *
 * Cross-references:
 *   - REQ-UI-114 (predicates), REQ-UI-115 (grouping), REQ-UI-116
 *     (persistent), REQ-UI-117 (inline errors, no request while invalid).
 *   - REQ-UI-059 (every affordance keyboard-operable).
 */
import * as Popover from "@radix-ui/react-popover";
import { Plus, Warning, X } from "@phosphor-icons/react";
import { createContext, useContext, useState } from "react";
import {
  type ComparisonNode,
  type GroupNode,
  OPERATOR_LABELS,
  type PredicateNode,
  type QueryAttribute,
  type QueryOperator,
  appendChild,
  newComparison,
  newGroup,
  operandCount,
  removeNode,
  retargetComparison,
  setOperand,
  setOperator,
  updateNode,
} from "@/query/query-model";
import type { ProducerOption } from "@/lib/use-producer-names";
import { cn } from "@/lib/utils";

export interface QueryBuilderProps {
  /** The editable predicate root. */
  readonly root: GroupNode;
  /** Filterable attributes of the selected type, in catalog order. */
  readonly attributes: readonly QueryAttribute[];
  /** Per-node validation messages, keyed by node id. */
  readonly errors: Readonly<Record<string, string>>;
  readonly onChange: (next: GroupNode) => void;
  /**
   * The platform inventory, so a ``producer_id`` operand is picked by name
   * rather than typed as a registry UUID (REQ-UI-188). Empty (the default)
   * falls back to the free-text field — an empty picker is a dead end.
   */
  readonly producerOptions?: readonly ProducerOption[];
  readonly testId?: string;
}

/**
 * The platform inventory, ambient to the chip tree.
 *
 * A context rather than a prop because the predicate tree is **recursive**:
 * threading it would add a parameter to every node renderer between the root
 * and the operand slot, none of which has any use for it. It is reference data
 * about the deployment, not part of the document being edited, so it is not
 * state this controlled component owns — which is the distinction the module
 * docstring's "no timing of its own" is really drawing.
 */
const ProducerOptionsContext = createContext<readonly ProducerOption[]>([]);

/**
 * The attribute whose operand is a platform.
 *
 * Named here and read by :func:`OperandInput`, matching the identification
 * ``query-columns.tsx`` already makes when it renders this column as a display
 * name. The two must agree: the picker commits an id that the results table
 * then renders back as the name that was picked.
 */
const PRODUCER_ATTRIBUTE = "producer_id";

/** Sentinel option value that flips the picker to free-text entry. */
const ENTER_ID_MANUALLY = "__enter_id__";

/** Render the predicate builder. */
export function QueryBuilder({
  root,
  attributes,
  errors,
  onChange,
  producerOptions = [],
  testId = "query-builder",
}: QueryBuilderProps): React.ReactElement {
  const byName = new Map(attributes.map((attribute) => [attribute.name, attribute]));

  const addCondition = (groupId: string): void => {
    const first = attributes[0];
    onChange(
      appendChild(
        root,
        groupId,
        first === undefined ? newComparison() : retargetComparison(newComparison(), first),
      ),
    );
  };

  const addGroup = (groupId: string): void => {
    const first = attributes[0];
    const seed = first === undefined ? newComparison() : retargetComparison(newComparison(), first);
    onChange(appendChild(root, groupId, newGroup("or", [seed])));
  };

  return (
    <ProducerOptionsContext.Provider value={producerOptions}>
      <div
        className="flex flex-wrap items-start gap-1.5"
        data-testid={testId}
        role="group"
        aria-label="Query conditions"
      >
        {root.children.map((child) => (
          <PredicateNodeView
            key={child.id}
            node={child}
            attributesByName={byName}
            attributes={attributes}
            errors={errors}
            root={root}
            onChange={onChange}
            onAddCondition={addCondition}
            onAddGroup={addGroup}
            testId={testId}
          />
        ))}
        <AddButtons
          onAddCondition={() => addCondition(root.id)}
          onAddGroup={() => addGroup(root.id)}
          disabled={attributes.length === 0}
          testId={`${testId}-root`}
        />
      </div>
    </ProducerOptionsContext.Provider>
  );
}

interface NodeViewProps {
  node: PredicateNode;
  attributesByName: ReadonlyMap<string, QueryAttribute>;
  attributes: readonly QueryAttribute[];
  errors: Readonly<Record<string, string>>;
  root: GroupNode;
  onChange: (next: GroupNode) => void;
  onAddCondition: (groupId: string) => void;
  onAddGroup: (groupId: string) => void;
  testId: string;
}

/** Dispatch on node kind — a chip or a nested group container. */
function PredicateNodeView(props: NodeViewProps): React.ReactElement {
  const { node } = props;
  if (node.kind === "group") {
    return <GroupView {...props} node={node} />;
  }
  return <ChipView {...props} node={node} />;
}

/** A nested boolean group: operator toggle, children, add affordances. */
function GroupView({
  node,
  attributesByName,
  attributes,
  errors,
  root,
  onChange,
  onAddCondition,
  onAddGroup,
  testId,
}: NodeViewProps & { node: GroupNode }): React.ReactElement {
  const error = errors[node.id];
  return (
    <div
      className={cn(
        "flex flex-wrap items-start gap-1.5 rounded border px-1.5 py-1",
        error === undefined ? "border-border" : "border-destructive",
      )}
      data-testid={`${testId}-group-${node.id}`}
      role="group"
      aria-label={`${node.op === "and" ? "All" : "Any"} of these conditions`}
    >
      <button
        type="button"
        onClick={() =>
          onChange(
            updateNode(root, node.id, (current) =>
              current.kind === "group"
                ? { ...current, op: current.op === "and" ? "or" : "and" }
                : current,
            ),
          )
        }
        aria-label={`Group matches ${node.op === "and" ? "all" : "any"} conditions — activate to switch`}
        title="Switch between AND and OR"
        data-testid={`${testId}-group-op-${node.id}`}
        className="rounded bg-bg-elevated px-1.5 py-0.5 text-meta uppercase tracking-wide text-brand hover:bg-bg-surface focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
      >
        {node.op}
      </button>
      {node.children.map((child) => (
        <PredicateNodeView
          key={child.id}
          node={child}
          attributesByName={attributesByName}
          attributes={attributes}
          errors={errors}
          root={root}
          onChange={onChange}
          onAddCondition={onAddCondition}
          onAddGroup={onAddGroup}
          testId={testId}
        />
      ))}
      <AddButtons
        onAddCondition={() => onAddCondition(node.id)}
        onAddGroup={() => onAddGroup(node.id)}
        disabled={attributes.length === 0}
        testId={`${testId}-${node.id}`}
      />
      <button
        type="button"
        onClick={() => onChange(removeNode(root, node.id))}
        aria-label="Remove group"
        title="Remove group"
        data-testid={`${testId}-remove-${node.id}`}
        className="rounded p-1 text-text-muted hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
      >
        <X size={10} aria-hidden="true" />
      </button>
      {error !== undefined && (
        <p className="w-full text-meta text-destructive" data-testid={`${testId}-error-${node.id}`}>
          {error}
        </p>
      )}
    </div>
  );
}

/** Human-readable summary of a chip's operand(s) for the closed chip. */
function operandSummary(node: ComparisonNode, attribute: QueryAttribute | undefined): string {
  if (node.operator === "is_null" || node.operator === "is_not_null") return "";
  const raw = node.operands.map((value) => value.trim());
  if (raw.every((value) => value === "")) return "…";
  if (node.operator === "between") return `${raw[0] || "…"} – ${raw[1] || "…"}`;
  if (node.operator === "within_bbox") return raw.join(", ");
  const value = raw[0] ?? "";
  if (attribute?.kind === "enum") {
    const parsed = Number.parseInt(value, 10);
    const match = attribute.enum_values?.find((option) => option.value === parsed);
    if (match !== undefined) return match.label;
  }
  return value;
}

/** One condition chip + its popover editor. */
function ChipView({
  node,
  attributesByName,
  attributes,
  errors,
  root,
  onChange,
  testId,
}: NodeViewProps & { node: ComparisonNode }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const attribute = attributesByName.get(node.attribute);
  const error = errors[node.id];
  const label = attribute?.label ?? node.attribute ?? "";
  const operatorLabel = node.operator === "" ? "" : OPERATOR_LABELS[node.operator];

  const update = (next: ComparisonNode): void => {
    onChange(updateNode(root, node.id, () => next));
  };

  return (
    <span className="inline-flex items-center" data-testid={`${testId}-chip-${node.id}`}>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            aria-label={`Edit condition: ${label} ${operatorLabel} ${operandSummary(node, attribute)}`}
            data-testid={`${testId}-chip-open-${node.id}`}
            className={cn(
              "flex items-center gap-1 rounded-l border px-2 py-1 text-body-sm",
              "focus:outline-none focus-visible:ring-1 focus-visible:ring-accent",
              error === undefined
                ? "border-border bg-bg-surface text-text-primary hover:bg-bg-elevated"
                : "border-destructive bg-bg-surface text-destructive",
            )}
          >
            {error !== undefined && <Warning size={12} aria-hidden="true" />}
            <span className="font-medium">{label || "Choose attribute"}</span>
            <span className="text-text-secondary">{operatorLabel}</span>
            <span className="font-mono">{operandSummary(node, attribute)}</span>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            side="bottom"
            align="start"
            sideOffset={4}
            className="z-50 w-72 rounded-md border border-border bg-bg-overlay p-3 shadow-lg"
            data-testid={`${testId}-editor-${node.id}`}
            aria-label="Edit condition"
          >
            <ChipEditor
              node={node}
              attribute={attribute}
              attributes={attributes}
              error={error}
              onChange={update}
              testId={testId}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <button
        type="button"
        onClick={() => onChange(removeNode(root, node.id))}
        aria-label={`Remove condition ${label}`}
        title="Remove condition"
        data-testid={`${testId}-remove-${node.id}`}
        className={cn(
          "rounded-r border border-l-0 px-1 py-1 text-text-muted",
          "hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent",
          error === undefined ? "border-border bg-bg-surface" : "border-destructive bg-bg-surface",
        )}
      >
        <X size={10} aria-hidden="true" />
      </button>
    </span>
  );
}

interface ChipEditorProps {
  node: ComparisonNode;
  attribute: QueryAttribute | undefined;
  attributes: readonly QueryAttribute[];
  error: string | undefined;
  onChange: (next: ComparisonNode) => void;
  testId: string;
}

/** The popover body: attribute, operator, and kind-appropriate operands. */
function ChipEditor({
  node,
  attribute,
  attributes,
  error,
  onChange,
  testId,
}: ChipEditorProps): React.ReactElement {
  const slots = node.operator === "" ? 0 : operandCount(node.operator);
  return (
    <div className="flex flex-col gap-2 text-body-sm">
      <label className="flex flex-col gap-1">
        <span className="text-meta uppercase tracking-wide text-text-secondary">Attribute</span>
        <select
          value={node.attribute}
          onChange={(event) => {
            const next = attributes.find((a) => a.name === event.target.value);
            if (next !== undefined) onChange(retargetComparison(node, next));
          }}
          data-testid={`${testId}-attribute-${node.id}`}
          className="rounded border border-border bg-bg-base px-1.5 py-1 text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
        >
          {node.attribute === "" && <option value="">Choose an attribute…</option>}
          {attributes.map((option) => (
            <option key={option.name} value={option.name}>
              {option.label}
              {option.indexed ? "" : " (unindexed)"}
            </option>
          ))}
        </select>
      </label>

      {attribute !== undefined && (
        <label className="flex flex-col gap-1">
          <span className="text-meta uppercase tracking-wide text-text-secondary">Operator</span>
          <select
            value={node.operator}
            onChange={(event) => onChange(setOperator(node, event.target.value as QueryOperator))}
            data-testid={`${testId}-operator-${node.id}`}
            className="rounded border border-border bg-bg-base px-1.5 py-1 text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
          >
            {node.operator === "" && <option value="">Choose an operator…</option>}
            {attribute.operators.map((operator) => (
              <option key={operator} value={operator}>
                {OPERATOR_LABELS[operator as QueryOperator] ?? operator}
              </option>
            ))}
          </select>
        </label>
      )}

      {attribute !== undefined && slots > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-meta uppercase tracking-wide text-text-secondary">
            {node.operator === "in" || node.operator === "not_in"
              ? "Values (comma-separated)"
              : "Value"}
          </span>
          <div className="flex flex-wrap gap-1">
            {Array.from({ length: slots }, (_, index) => (
              <OperandInput
                key={index}
                node={node}
                index={index}
                slots={slots}
                attribute={attribute}
                onChange={onChange}
                testId={testId}
              />
            ))}
          </div>
        </div>
      )}

      {error !== undefined && (
        <p
          role="alert"
          className="text-meta text-destructive"
          data-testid={`${testId}-error-${node.id}`}
        >
          {error}
        </p>
      )}
    </div>
  );
}

interface OperandInputProps {
  node: ComparisonNode;
  index: number;
  slots: number;
  attribute: QueryAttribute;
  onChange: (next: ComparisonNode) => void;
  testId: string;
}

/** One operand slot, typed by the attribute's kind. */
function OperandInput({
  node,
  index,
  slots,
  attribute,
  onChange,
  testId,
}: OperandInputProps): React.ReactElement {
  const value = node.operands[index] ?? "";
  const producerOptions = useContext(ProducerOptionsContext);
  const commit = (next: string): void => onChange(setOperand(node, index, next));
  const id = `${testId}-operand-${node.id}-${index}`;
  const shared =
    "flex-1 rounded border border-border bg-bg-base px-1.5 py-1 text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent";
  const isList = node.operator === "in" || node.operator === "not_in";
  const bboxLabels = ["Min lon", "Min lat", "Max lon", "Max lat"];
  const ariaLabel =
    slots === 4
      ? bboxLabels[index]
      : slots === 2
        ? index === 0
          ? "Lower value"
          : "Upper value"
        : "Value";

  // The same argument the enum branch makes, for an open domain: the operator
  // should never have to know a platform's registry UUID. Skipped for the list
  // operators, where a single-select would admit one member of a set.
  if (attribute.name === PRODUCER_ATTRIBUTE && !isList && producerOptions.length > 0) {
    return (
      <ProducerOperand
        value={value}
        options={producerOptions}
        ariaLabel={ariaLabel}
        onCommit={commit}
        testId={id}
        className={shared}
      />
    );
  }

  // An enum with a closed domain gets a picker rather than a free-text
  // integer — the operator should never have to know the proto value.
  if (attribute.kind === "enum" && !isList) {
    return (
      <select
        value={value}
        aria-label={ariaLabel}
        onChange={(event) => commit(event.target.value)}
        data-testid={id}
        className={shared}
      >
        <option value="">Choose…</option>
        {attribute.enum_values?.map((option) => (
          <option key={option.value} value={String(option.value)}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (attribute.kind === "boolean") {
    return (
      <select
        value={value}
        aria-label={ariaLabel}
        onChange={(event) => commit(event.target.value)}
        data-testid={id}
        className={shared}
      >
        <option value="">Choose…</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }

  const inputType =
    isList || attribute.kind === "string" || attribute.kind === "id"
      ? "text"
      : attribute.kind === "timestamp"
        ? "datetime-local"
        : "number";

  return (
    <input
      type={inputType}
      value={value}
      aria-label={ariaLabel}
      placeholder={slots === 4 ? bboxLabels[index] : undefined}
      onChange={(event) => commit(event.target.value)}
      data-testid={id}
      className={shared}
    />
  );
}

/**
 * The ``producer_id`` operand: a platform picker with a free-text escape.
 *
 * Two things a plain ``<select>`` would get wrong, both of which would change
 * an operator's query without telling them (CLAUDE.md §8.1):
 *
 * - **An id the inventory does not hold** — pasted from an audit event, or a
 *   producer since purged from the registry — is kept and labelled, rather
 *   than silently falling out of the predicate when the chip re-renders.
 * - **A closed list is a dead end.** The last option hands the operator the
 *   raw field back, and the field hands the picker back, so neither mode
 *   traps them.
 */
function ProducerOperand({
  value,
  options,
  ariaLabel,
  onCommit,
  testId,
  className,
}: {
  value: string;
  options: readonly ProducerOption[];
  ariaLabel: string;
  onCommit: (next: string) => void;
  testId: string;
  className: string;
}): React.ReactElement {
  const known = options.some((option) => option.id === value);
  // An unknown id stays in the PICKER, annotated, rather than flipping the
  // control to free text: the annotation is the part that carries information
  // ("this filter names a producer the registry has no record of"), and a bare
  // text field showing the same id says nothing at all.
  const [manual, setManual] = useState(false);

  if (manual) {
    return (
      <span className="flex flex-1 items-center gap-1">
        <input
          type="text"
          value={value}
          aria-label={ariaLabel}
          placeholder="Producer ID"
          onChange={(event) => onCommit(event.target.value)}
          data-testid={testId}
          className={className}
        />
        <button
          type="button"
          onClick={() => setManual(false)}
          data-testid={`${testId}-pick`}
          className="rounded border border-border px-1.5 py-1 text-body-sm text-text-secondary hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
        >
          Pick
        </button>
      </span>
    );
  }

  return (
    <select
      value={value}
      aria-label={ariaLabel}
      onChange={(event) => {
        if (event.target.value === ENTER_ID_MANUALLY) {
          setManual(true);
          return;
        }
        onCommit(event.target.value);
      }}
      data-testid={testId}
      className={className}
    >
      <option value="">Choose…</option>
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.name}
        </option>
      ))}
      {value !== "" && !known && <option value={value}>{value} (not in inventory)</option>}
      <option value={ENTER_ID_MANUALLY}>Enter an ID manually…</option>
    </select>
  );
}

interface AddButtonsProps {
  onAddCondition: () => void;
  onAddGroup: () => void;
  disabled: boolean;
  testId: string;
}

/** The "add condition" / "add group" pair for one group scope. */
function AddButtons({
  onAddCondition,
  onAddGroup,
  disabled,
  testId,
}: AddButtonsProps): React.ReactElement {
  const base =
    "flex items-center gap-1 rounded border border-dashed border-border px-1.5 py-1 text-body-sm text-text-secondary hover:border-brand hover:text-text-primary disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent";
  return (
    <span className="flex items-center gap-1">
      <button
        type="button"
        onClick={onAddCondition}
        disabled={disabled}
        data-testid={`${testId}-add-condition`}
        className={base}
      >
        <Plus size={10} aria-hidden="true" />
        Condition
      </button>
      <button
        type="button"
        onClick={onAddGroup}
        disabled={disabled}
        data-testid={`${testId}-add-group`}
        className={base}
      >
        <Plus size={10} aria-hidden="true" />
        Group
      </button>
    </span>
  );
}
